import { isNonEmptyArray, maxInstant, type AirServerError } from '@airreading/common';
import { SingleUserFull, updateUserIdentitiesOptionsSchema, UserIdentityProviderKey } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { EditableInfoCard, SubmitProps } from '@/components/editable-info-card';
import { getUsers, updateUser } from '@/lib/api-calls';
import { QueryKeys } from '@/lib/queries';
import { buildUserIdentitiesDefaultValues, WithUserIdentities } from '@/lib/user-identity-utils';

import { airServerErrorFrom } from '@/lib/error-handling';
import { UserIdentityProviderFields } from './user-identity-provider-fields';

const editUserIdentitiesFormSchema = updateUserIdentitiesOptionsSchema;
export type EditUserIdentitiesForm = z.infer<typeof editUserIdentitiesFormSchema>;

function identityKeyForUser(user: WithUserIdentities): string {
  const updatedAtInstants = user.userIdentities.map((identity) => identity.updatedAtInstant);
  if (!isNonEmptyArray(updatedAtInstants)) {
    return '0';
  }

  return String(maxInstant(...updatedAtInstants).epochMilliseconds);
}

export interface SsoInfoCardProps {
  user: WithUserIdentities;
  onSubmit: (props: SubmitProps<EditUserIdentitiesForm>) => unknown;
}

export function SsoInfoCard({ user, onSubmit }: SsoInfoCardProps) {
  const defaultValues = buildUserIdentitiesDefaultValues(user);
  const { google, edlink } = defaultValues;

  return (
    <EditableInfoCard
      key={`sso-info-${user.id}-${identityKeyForUser(user)}`}
      headerText="SSO info"
      formSchema={editUserIdentitiesFormSchema}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
    >
      {({ form, editing }) => (
        <div className="flex flex-col gap-2">
          <UserIdentityProviderFields
            providerKey={UserIdentityProviderKey.google}
            title="Google workspace user identity"
            form={form}
            editing={editing}
            provider={google}
          />
          <UserIdentityProviderFields
            providerKey={UserIdentityProviderKey.edlink}
            title="Edlink user identity"
            form={form}
            editing={editing}
            provider={edlink}
          />
        </div>
      )}
    </EditableInfoCard>
  );
}

export interface SsoEditableInfoCardProps {
  user: WithUserIdentities;
  onSuccess: (newUser: SingleUserFull) => void;
  onError: (error?: AirServerError) => void;
}

export function SsoEditableInfoCard({ user, onSuccess, onError }: SsoEditableInfoCardProps) {
  const queryClient = useQueryClient();
  const { mutateAsync: updateUserMutate } = useMutation({
    mutationFn: updateUser,
  });

  const onSubmit = ({ formData, disableEditing }: SubmitProps<EditUserIdentitiesForm>) => {
    return updateUserMutate({
      userId: user.id,
      data: { userIdentities: formData },
    })
      .then((newUser) => {
        disableEditing();
        queryClient.invalidateQueries({ queryKey: QueryKeys.getUser(user.id) });
        queryClient.invalidateQueries(getUsers());
        onSuccess(newUser);
      })
      .catch((e) => {
        onError(airServerErrorFrom(e));
      });
  };
  return <SsoInfoCard user={user} onSubmit={onSubmit} />;
}
